// @ts-nocheck -- TypeScript migration in progress: bulk-renamed from .js and not
// yet typed. Remove this directive and add real types when converting this
// module. Backlog: grep -rl "@ts-nocheck" src. See docs/typescript-migration.md.
import {
  averageValues,
  extentFromValues,
} from "../core/math.js";
import {
  cylinderKeys,
} from "../core/schema.js";
import {
  clampScore,
  finiteValues,
  median,
  pointCylinderValues,
  rankedQuantile,
} from "./utils.js";
import {
  electricalParameters,
} from "./detectors/electrical.js";
import {
  startChargeParameters,
} from "./detectors/start-charge.js";

function sampleIntervalSeconds(points) {
  const diffs = [];
  for (let index = 1; index < points.length; index += 1) {
    const previous = points[index - 1].elapsedSeconds;
    const current = points[index].elapsedSeconds;
    if (Number.isFinite(previous) && Number.isFinite(current) && current > previous) {
      diffs.push(current - previous);
    }
  }
  return rankedQuantile(diffs, 0.5) ?? 1;
}

function flagRuns(flags) {
  const runs = [];
  let start = 0;
  while (start < flags.length) {
    let end = start + 1;
    while (end < flags.length && flags[end] === flags[start]) end += 1;
    runs.push({ start, end, value: flags[start], count: end - start });
    start = end;
  }
  return runs;
}

function sustainedIndex(flags, startIndex = 0, sampleInterval = 1, seconds = 20) {
  const needed = Math.max(2, Math.round(seconds / Math.max(sampleInterval, 0.25)));
  let streak = 0;
  for (let index = Math.max(0, startIndex); index < flags.length; index += 1) {
    streak = flags[index] ? streak + 1 : 0;
    if (streak >= needed) return index - streak + 1;
  }
  return -1;
}

function electricalPowerRunningState(point) {
  const config = electricalParameters.runningState;
  const rpmRunning = Number.isFinite(point?.rpm) && point.rpm > config.rpm;
  const fuelRunning = Number.isFinite(point?.fuelFlow) && point.fuelFlow > config.fuelFlowGph;
  const hasPowerSignal = Number.isFinite(point?.rpm) || Number.isFinite(point?.fuelFlow);
  if (!hasPowerSignal) return null;
  return rpmRunning || fuelRunning;
}

function electricalEgtAverage(point) {
  const cylinderValues = cylinderKeys("egt").map((key) => point?.[key]).filter(Number.isFinite);
  if (cylinderValues.length) return averageValues(cylinderValues);
  return Number.isFinite(point?.egt) ? point.egt : null;
}

function pointsInElapsedWindow(data, startTime, endTime, predicate = () => true) {
  return data.filter((point) => {
    const elapsed = point.elapsedSeconds;
    return Number.isFinite(elapsed) && elapsed >= startTime && elapsed <= endTime && predicate(point);
  });
}

function medianInElapsedWindow(data, startTime, endTime, key) {
  return median(pointsInElapsedWindow(data, startTime, endTime, (point) => Number.isFinite(point[key]))
    .map((point) => point[key]));
}

function extentInElapsedWindow(data, startTime, endTime, key) {
  return extentFromValues(pointsInElapsedWindow(data, startTime, endTime, (point) => Number.isFinite(point[key]))
    .map((point) => point[key]));
}

function pointWithMinValue(points, key) {
  return points.reduce((best, point) => {
    if (!Number.isFinite(point[key])) return best;
    if (!best || point[key] < best[key]) return point;
    return best;
  }, null);
}

function pointWithMaxValue(points, key) {
  return points.reduce((best, point) => {
    if (!Number.isFinite(point[key])) return best;
    if (!best || point[key] > best[key]) return point;
    return best;
  }, null);
}

function firstSustainedRunningIndex(data) {
  const config = electricalParameters.runningDetection;
  const states = data.map(electricalPowerRunningState);
  const knownCount = states.filter((state) => state !== null).length;
  if (knownCount < Math.min(config.minKnownSamples, Math.max(2, data.length * config.minKnownShare))) return -1;
  const flags = states.map((state) => state === true);
  return sustainedIndex(flags, 0, sampleIntervalSeconds(data), config.sustainedSeconds);
}

function logBeginsWithRunningEngine(data, startIndex) {
  const config = electricalParameters.initialRunning;
  if (startIndex < 0) return false;
  const firstElapsed = data.find((point) => Number.isFinite(point.elapsedSeconds))?.elapsedSeconds ?? 0;
  const startTime = data[startIndex]?.elapsedSeconds ?? startIndex;
  if (!Number.isFinite(startTime) || startTime > firstElapsed + config.startGraceSeconds) return false;

  const initialRows = pointsInElapsedWindow(data, firstElapsed, firstElapsed + config.initialWindowSeconds);
  if (initialRows.length < config.minRows) return false;
  const rpmMedian = median(initialRows.map((point) => point.rpm).filter(Number.isFinite));
  const fuelMedian = median(initialRows.map((point) => point.fuelFlow).filter(Number.isFinite));
  const oilMedian = median(initialRows.map((point) => point.oilPressure).filter(Number.isFinite));
  const egtMedian = median(initialRows.map(electricalEgtAverage).filter(Number.isFinite));
  const chtMedian = median(initialRows.map((point) => averageValues(pointCylinderValues(point, cylinderKeys("cht")))).filter(Number.isFinite));
  const busMedian = median(initialRows.map((point) => point.buss).filter(Number.isFinite));
  const runningEvidence = [
    Number.isFinite(rpmMedian) ? rpmMedian > config.rpm : null,
    Number.isFinite(fuelMedian) ? fuelMedian > config.fuelFlowGph : null,
    Number.isFinite(oilMedian) ? oilMedian > config.oilPressurePsi : null,
    Number.isFinite(egtMedian) ? egtMedian > config.egtF : null,
    Number.isFinite(chtMedian) ? chtMedian > config.chtF : null,
  ].filter((value) => value !== null);
  const busAlreadyCharging = Number.isFinite(busMedian) && busMedian >= config.chargingBusVolts;
  return runningEvidence.length >= config.minEvidenceSignals
    && runningEvidence.filter(Boolean).length >= Math.min(config.minPassingSignals, runningEvidence.length)
    && (busAlreadyCharging || runningEvidence.filter(Boolean).length >= config.strongPassingSignals);
}

function logStartsWithRunningEngine(data) {
  return logBeginsWithRunningEngine(data, 0);
}

function firstSustainedRpmStartIndex(data) {
  const config = electricalParameters.runningDetection;
  const rows = data.map((point, index) => ({
    index,
    time: point.elapsedSeconds ?? index,
    rpm: point.rpm,
  })).filter((row) => Number.isFinite(row.time) && Number.isFinite(row.rpm));
  if (rows.length < config.minKnownSamples) return -1;

  const interval = sampleIntervalSeconds(data);
  const runThreshold = electricalParameters.runningState.rpm;
  const runningFlags = data.map((point) => Number.isFinite(point.rpm) && point.rpm > runThreshold);
  const firstRunning = sustainedIndex(runningFlags, 0, interval, config.sustainedSeconds);
  if (firstRunning < 0) return -1;

  const startTime = data[firstRunning]?.elapsedSeconds ?? firstRunning;
  const prior = pointsInElapsedWindow(data, startTime - config.priorWindowStartSeconds, startTime - config.priorWindowEndSeconds, (point) => Number.isFinite(point.rpm));
  const after = pointsInElapsedWindow(data, startTime, startTime + config.afterWindowSeconds, (point) => Number.isFinite(point.rpm));
  const priorMedian = median(prior.map((point) => point.rpm));
  const afterMedian = median(after.map((point) => point.rpm));
  const priorLooksStopped = prior.some((point) => point.rpm <= config.stoppedRpm) || (Number.isFinite(priorMedian) && priorMedian <= config.stoppedMedianRpm);
  if (!priorLooksStopped && logBeginsWithRunningEngine(data, firstRunning)) return -1;
  const afterLooksRunning = Number.isFinite(afterMedian) && afterMedian > runThreshold;
  return priorLooksStopped && afterLooksRunning ? firstRunning : -1;
}

function runningIndexHasStartContext(data, index) {
  const config = startChargeParameters.startContext;
  if (index < 0) return false;
  const priorStates = data.slice(0, index).map(electricalPowerRunningState);
  if (priorStates.some((state) => state === false)) return true;
  const startTime = data[index]?.elapsedSeconds ?? index;
  if (!Number.isFinite(startTime)) return false;
  if (data[index]?.phase === "Startup") return true;
  const nearbyStartup = pointsInElapsedWindow(data, startTime - config.nearbyStartupSeconds, startTime + config.nearbyStartupSeconds)
    .some((point) => point.phase === "Startup");
  if (nearbyStartup) return true;
  const before = median(pointsInElapsedWindow(data, startTime - config.egtBeforeStartSeconds, startTime - config.egtBeforeEndSeconds)
    .map(electricalEgtAverage)
    .filter(Number.isFinite));
  const after = extentFromValues(pointsInElapsedWindow(data, startTime, startTime + config.egtAfterSeconds)
    .map(electricalEgtAverage)
    .filter(Number.isFinite));
  return Number.isFinite(before) && after && after.max - before >= config.minEgtRiseF;
}

function firstEgtLightoffIndex(data) {
  const config = startChargeParameters.egtStart;
  const rows = data.map((point, index) => ({
    index,
    time: point.elapsedSeconds ?? index,
    value: electricalEgtAverage(point),
  })).filter((row) => Number.isFinite(row.time) && Number.isFinite(row.value));
  if (rows.length < config.minRows) return -1;

  const firstTime = rows[0].time;
  const searchEndTime = firstTime + config.searchSeconds;
  const initialValues = rows.slice(0, Math.min(rows.length, config.initialSamples)).map((row) => row.value);
  const initialMedian = median(initialValues);

  for (const row of rows) {
    if (row.time > searchEndTime) break;
    const beforeValues = rows
      .filter((candidate) => candidate.time >= row.time - config.beforeWindowStartSeconds && candidate.time <= row.time - config.beforeWindowEndSeconds)
      .map((candidate) => candidate.value);
    const afterValues = rows
      .filter((candidate) => candidate.time >= row.time && candidate.time <= row.time + config.afterWindowSeconds)
      .map((candidate) => candidate.value);
    if (afterValues.length < config.minAfterSamples) continue;
    const baseline = beforeValues.length >= config.minBeforeSamples ? median(beforeValues) : initialMedian;
    const afterExtent = extentFromValues(afterValues);
    if (!Number.isFinite(baseline) || !afterExtent) continue;
    const currentLift = row.value - baseline;
    const futureLift = afterExtent.max - baseline;
    if (currentLift >= config.currentLiftF && futureLift >= config.futureLiftF) return row.index;
  }

  return -1;
}

function detectElectricalStartEvent(data) {
  const contextConfig = startChargeParameters.context;
  const startContextConfig = startChargeParameters.startContext;
  const rpmIndex = firstSustainedRpmStartIndex(data);
  if (rpmIndex >= 0) {
    const point = data[rpmIndex];
    return {
      index: rpmIndex,
      startTime: point?.elapsedSeconds ?? rpmIndex,
      basis: "RPM start transition",
      egtRise: null,
    };
  }
  if (logStartsWithRunningEngine(data)) {
    const firstElapsed = data.find((point) => Number.isFinite(point.elapsedSeconds))?.elapsedSeconds ?? 0;
    const startupPhaseNearby = pointsInElapsedWindow(data, firstElapsed, firstElapsed + contextConfig.startupPhaseNearbySeconds)
      .some((point) => point.phase === "Startup");
    if (!startupPhaseNearby) return null;
  }

  const runningIndex = firstSustainedRunningIndex(data);
  const candidates = [
    runningIndexHasStartContext(data, runningIndex) ? runningIndex : -1,
    firstEgtLightoffIndex(data),
  ]
    .filter((index) => index >= 0)
    .sort((a, b) => a - b);
  let index = candidates[0] ?? -1;
  let basis = candidates.length ? "engine light-off" : "";

  if (index < 0) {
    const startupBusPoints = data
      .map((point, pointIndex) => ({ point, index: pointIndex }))
      .filter(({ point }) => point.phase === "Startup" && Number.isFinite(point.buss));
    const minStartup = startupBusPoints.reduce((best, item) => {
      if (!best || item.point.buss < best.point.buss) return item;
      return best;
    }, null);
    if (minStartup) {
      index = minStartup.index;
      basis = "startup voltage sag";
    }
  }

  if (index < 0) return null;
  const point = data[index];
  const startTime = point?.elapsedSeconds ?? index;
  if (!Number.isFinite(startTime)) return null;
  if (logStartsWithRunningEngine(data)) {
    const priorStates = data.slice(0, index).map(electricalPowerRunningState);
    const priorOffSamples = priorStates.filter((state) => state === false).length;
    if (!priorOffSamples) return null;
  }
  const egtBefore = median(pointsInElapsedWindow(data, startTime - startContextConfig.egtBeforeStartSeconds, startTime - startContextConfig.egtBeforeEndSeconds)
    .map(electricalEgtAverage)
    .filter(Number.isFinite));
  const egtAfterExtent = extentFromValues(pointsInElapsedWindow(data, startTime, startTime + startContextConfig.egtAfterSeconds)
    .map(electricalEgtAverage)
    .filter(Number.isFinite));
  const egtRise = Number.isFinite(egtBefore) && egtAfterExtent ? egtAfterExtent.max - egtBefore : null;

  return {
    index,
    startTime,
    basis,
    egtRise,
  };
}

function electricalStartExclusionWindows(data) {
  const config = startChargeParameters.exclusionWindows;
  const contextConfig = startChargeParameters.context;
  if (!data.length) return [];
  const interval = sampleIntervalSeconds(data);
  const states = data.map(electricalPowerRunningState);
  const flags = states.map((state) => state === true);
  const minRunSamples = Math.max(2, Math.round(config.minRunSeconds / Math.max(interval, 0.25)));
  const firstElapsed = data.find((point) => Number.isFinite(point.elapsedSeconds))?.elapsedSeconds ?? 0;

  return flagRuns(flags)
    .filter((run) => run.value && run.count >= minRunSamples)
    .map((run) => {
      const startTime = data[run.start]?.elapsedSeconds ?? run.start;
      if (!Number.isFinite(startTime)) return null;
      const priorStates = pointsInElapsedWindow(data, startTime - config.priorWindowStartSeconds, startTime - config.priorWindowEndSeconds)
        .map(electricalPowerRunningState);
      const priorOffSamples = priorStates.filter((state) => state === false).length;
      const nearbyStartupPhase = pointsInElapsedWindow(data, startTime - config.nearbyStartupSeconds, startTime + config.nearbyStartupSeconds)
        .some((point) => point.phase === "Startup");
      const egtBefore = median(pointsInElapsedWindow(data, startTime - config.egtBeforeStartSeconds, startTime - config.egtBeforeEndSeconds)
        .map(electricalEgtAverage)
        .filter(Number.isFinite));
      const egtAfter = extentFromValues(pointsInElapsedWindow(data, startTime, startTime + config.egtAfterSeconds)
        .map(electricalEgtAverage)
        .filter(Number.isFinite));
      const egtRise = Number.isFinite(egtBefore) && egtAfter ? egtAfter.max - egtBefore : null;
      const busBefore = medianInElapsedWindow(data, startTime - config.busBeforeStartSeconds, startTime - config.busBeforeEndSeconds, "buss");
      const busMin = pointWithMinValue(pointsInElapsedWindow(data, startTime - config.busSagBeforeSeconds, startTime + config.busSagAfterSeconds, (point) => Number.isFinite(point.buss)), "buss");
      const busAfter = medianInElapsedWindow(data, startTime + config.busAfterStartSeconds, startTime + config.busAfterEndSeconds, "buss");
      const busSag = Number.isFinite(busBefore) && Number.isFinite(busMin?.buss)
        ? busBefore - busMin.buss
        : null;
      const busRecovered = Number.isFinite(busAfter) && Number.isFinite(busMin?.buss) && busAfter - busMin.buss >= config.busRecoveryRiseVolts;
      const logBeganRunning = logStartsWithRunningEngine(data);
      const initialStartAllowed = startTime <= firstElapsed + contextConfig.logBeginningStartGraceSeconds && !logBeganRunning;
      const looksLikeStart = initialStartAllowed
        || priorOffSamples >= Math.max(2, Math.round(config.priorOffSeconds / Math.max(interval, 0.25)))
        || (nearbyStartupPhase && !logBeganRunning)
        || (Number.isFinite(egtRise) && egtRise >= startChargeParameters.startContext.minEgtRiseF)
        || (Number.isFinite(busSag) && busSag >= config.busRecoveryRiseVolts && busRecovered);
      if (!looksLikeStart) return null;
      return {
        startTime: Math.max(firstElapsed, startTime - config.startLeadSeconds),
        endTime: startTime + config.endAfterSeconds,
        startIndex: run.start,
        basis: startTime <= firstElapsed + contextConfig.logBeginningStartGraceSeconds ? "initial engine start" : "engine restart",
      };
    })
    .filter(Boolean);
}

function pointInElectricalStartWindow(point, windows) {
  const elapsed = point?.elapsedSeconds;
  return Number.isFinite(elapsed) && windows.some((window) => elapsed >= window.startTime && elapsed <= window.endTime);
}

function electricalAmpSpikeSummary(data, startTime) {
  const config = startChargeParameters.ampSpike;
  const ampValues = finiteValues(data, "amps");
  if (ampValues.length < config.minLoggedSamples) {
    return {
      available: false,
      hasSpike: false,
      spikeMagnitude: null,
      spikeDirection: "",
      note: "amps not logged",
    };
  }

  const postStartAmps = pointsInElapsedWindow(data, startTime, startTime + config.postStartSearchSeconds, (point) => Number.isFinite(point.amps));
  const firstPostStartAmp = postStartAmps[0];
  let early = pointsInElapsedWindow(data, startTime, startTime + config.earlyWindowSeconds, (point) => Number.isFinite(point.amps));
  let delayedSamples = false;
  if (early.length < config.minEarlySamples && firstPostStartAmp) {
    const anchor = firstPostStartAmp.elapsedSeconds;
    early = pointsInElapsedWindow(data, anchor, Math.min(anchor + config.earlyWindowSeconds, startTime + config.postStartSearchSeconds), (point) => Number.isFinite(point.amps));
    delayedSamples = anchor > startTime + config.earlyWindowSeconds;
  }
  if (early.length < config.minEarlySamples) {
    return {
      available: true,
      hasSpike: false,
      spikeMagnitude: null,
      spikeDirection: "",
      firstSampleTime: firstPostStartAmp?.elapsedSeconds ?? null,
      delayedSamples,
      note: "not enough post-start amps samples",
    };
  }

  const earlyStart = early[0]?.elapsedSeconds ?? startTime;
  const lateMedian = medianInElapsedWindow(data, Math.max(startTime + config.earlyWindowSeconds, earlyStart + config.earlyWindowSeconds), startTime + config.lateWindowEndSeconds, "amps")
    ?? medianInElapsedWindow(data, startTime + config.earlyWindowSeconds, startTime + config.fallbackLateWindowEndSeconds, "amps");
  const preMedian = medianInElapsedWindow(data, startTime - config.preWindowStartSeconds, startTime - config.preWindowEndSeconds, "amps");
  const reference = Number.isFinite(lateMedian) ? lateMedian : preMedian;
  if (!Number.isFinite(reference)) {
    return {
      available: true,
      hasSpike: false,
      spikeMagnitude: null,
      spikeDirection: "",
      firstSampleTime: firstPostStartAmp?.elapsedSeconds ?? null,
      delayedSamples,
      note: "no amp baseline around start",
    };
  }

  const earlyMaxPoint = pointWithMaxValue(early, "amps");
  const earlyMinPoint = pointWithMinValue(early, "amps");
  const positiveSpike = Number.isFinite(earlyMaxPoint?.amps) ? earlyMaxPoint.amps - reference : 0;
  const negativeSpike = Number.isFinite(earlyMinPoint?.amps) ? reference - earlyMinPoint.amps : 0;
  const spikeDirection = positiveSpike >= negativeSpike ? "positive" : "negative";
  const spikeMagnitude = Math.max(positiveSpike, negativeSpike, 0);
  const spikePoint = spikeDirection === "positive" ? earlyMaxPoint : earlyMinPoint;
  const threshold = Math.max(config.minSpikeAmps, Math.abs(reference) * config.referenceRatio);
  const hasSpike = spikeMagnitude >= threshold;
  const taperMagnitude = hasSpike && Number.isFinite(lateMedian)
    ? spikeMagnitude
    : null;

  return {
    available: true,
    hasSpike,
    spikeMagnitude,
    spikeDirection,
    spikeTime: spikePoint?.elapsedSeconds ?? null,
    peakAmps: spikePoint?.amps ?? null,
    referenceAmps: reference,
    lateMedian,
    preMedian,
    threshold,
    firstSampleTime: firstPostStartAmp?.elapsedSeconds ?? null,
    delayedSamples,
    taperMagnitude,
    note: hasSpike
      ? `${delayedSamples ? "delayed " : ""}${spikeDirection === "negative" ? "-" : "+"}${spikeMagnitude.toFixed(1)} A recharge spike`
      : "no meaningful post-start amp spike",
  };
}

function chargingRecoveryMeta(charging) {
  if (!charging) return "";
  const pieces = [];
  if (Number.isFinite(charging.recoveryMedian)) pieces.push(`recovered bus ${charging.recoveryMedian.toFixed(1)} V`);
  if (charging.amps?.available) {
    pieces.push(charging.amps.hasSpike
      ? charging.amps.note
      : charging.startAtLogBeginning
        ? "start current transient not captured"
        : "no amp spike");
  } else {
    pieces.push("voltage-only; amps not logged");
  }
  return pieces.join(" · ");
}

function chargingRecoveryNote(charging) {
  const meta = chargingRecoveryMeta(charging);
  if (!charging) return "";
  if (charging.status === "pass") return `Start charge pass: ${meta}`;
  if (charging.status === "fail") return `Start charge fail: ${meta}`;
  if (charging.status === "warning") return `Start charge watch: ${meta}`;
  if (charging.voltsRecovered) return `Start voltage recovered; ${charging.amps?.note ?? "amps not assessed"}`;
  return `Start charge not confirmed: ${meta}`;
}

function analyzeStartChargingRecovery(data, electricalContext) {
  const config = startChargeParameters.recovery;
  const scoring = startChargeParameters.scoring;
  const event = detectElectricalStartEvent(data);
  if (!event) return null;

  const {
    isTwentyEightVolt,
    lowLimit,
    runningStable,
    medianVoltage,
  } = electricalContext;
  const voltageScale = isTwentyEightVolt ? 2 : 1;
  const chargingLimit = isTwentyEightVolt ? config.chargingLimit28V : config.chargingLimit12V;
  const startTime = event.startTime;
  const firstElapsed = data.find((point) => Number.isFinite(point.elapsedSeconds))?.elapsedSeconds ?? 0;
  const startAtLogBeginning = startTime <= firstElapsed + startChargeParameters.context.startAtLogBeginningSeconds;
  const sagWindow = pointsInElapsedWindow(data, startTime - config.sagWindowSeconds, startTime + config.sagWindowSeconds, (point) => Number.isFinite(point.buss));
  const busMinPoint = pointWithMinValue(sagWindow, "buss");
  const busMin = busMinPoint?.buss ?? null;
  const busPreMedian = medianInElapsedWindow(data, startTime - config.preWindowStartSeconds, startTime - config.preWindowEndSeconds, "buss");
  const promptRecoveryMedian = medianInElapsedWindow(data, startTime + config.promptStartSeconds, startTime + config.promptEndSeconds, "buss")
    ?? medianInElapsedWindow(data, startTime + config.promptFallbackStartSeconds, startTime + config.promptFallbackEndSeconds, "buss");
  const sustainedRecoveryMedian = medianInElapsedWindow(data, startTime + config.sustainedStartSeconds, startTime + config.sustainedEndSeconds, "buss")
    ?? medianInElapsedWindow(data, startTime + config.sustainedFallbackStartSeconds, startTime + config.sustainedFallbackEndSeconds, "buss");
  const promptVoltsRecovered = Number.isFinite(promptRecoveryMedian) && promptRecoveryMedian >= chargingLimit;
  const sustainedVoltsRecovered = Number.isFinite(sustainedRecoveryMedian) && sustainedRecoveryMedian >= chargingLimit;
  const recoveryMedian = sustainedVoltsRecovered && !promptVoltsRecovered
    ? sustainedRecoveryMedian
    : promptRecoveryMedian ?? sustainedRecoveryMedian;
  const recoveryExtent = extentInElapsedWindow(data, startTime + config.extentStartSeconds, startTime + config.extentEndSeconds, "buss")
    ?? extentInElapsedWindow(data, startTime + config.extentStartSeconds, startTime + config.extentFallbackEndSeconds, "buss");
  const sagDepth = Number.isFinite(busPreMedian) && Number.isFinite(busMin) ? busPreMedian - busMin : null;
  const recoveryRise = Number.isFinite(recoveryMedian) && Number.isFinite(busMin) ? recoveryMedian - busMin : null;
  const riseRecovered = Number.isFinite(recoveryRise)
    && recoveryRise >= voltageScale * config.minRecoveryRiseVolts
    && recoveryMedian >= lowLimit + voltageScale * config.lowLimitMarginVolts;
  const voltsRecovered = Number.isFinite(recoveryMedian)
    && (
      promptVoltsRecovered
      || sustainedVoltsRecovered
      || (runningStable && medianVoltage >= chargingLimit)
      || riseRecovered
    );
  const delayedVoltsRecovered = sustainedVoltsRecovered && !promptVoltsRecovered;
  const postStartLow = Number.isFinite(recoveryMedian) && recoveryMedian < chargingLimit && !voltsRecovered;
  const lowVoltsMaintained = Number.isFinite(recoveryMedian) && !voltsRecovered
    && (
      recoveryMedian < lowLimit
      || (postStartLow && Number.isFinite(recoveryExtent?.min) && recoveryExtent.min < lowLimit)
    );
  const amps = electricalAmpSpikeSummary(data, startTime);

  let status = "indeterminate";
  let reason = "";
  let penalty = 0;
  if (lowVoltsMaintained && amps.available && !amps.hasSpike) {
    status = "fail";
    reason = "low volts maintained and no amp spike";
    penalty = scoring.failLowVoltsNoAmpPenalty;
  } else if (lowVoltsMaintained) {
    status = "warning";
    reason = amps.hasSpike ? "amps rose but bus stayed low" : "low volts maintained; amps unavailable";
    penalty = scoring.warningLowVoltsPenalty;
  } else if (voltsRecovered && amps.available && amps.hasSpike) {
    status = "pass";
    reason = delayedVoltsRecovered || amps.delayedSamples
      ? "bus and recharge current recovered after delay"
      : "bus recovered with recharge current spike";
    penalty = 0;
  } else if (voltsRecovered && amps.available && !amps.hasSpike && startAtLogBeginning) {
    status = "pass";
    reason = "bus was regulated when the log began; start current transient was not captured";
    penalty = 0;
  } else if (voltsRecovered && amps.available && !amps.hasSpike) {
    status = "warning";
    reason = delayedVoltsRecovered
      ? "bus recovered after delay without a clear amp spike"
      : "bus recovered without a clear amp spike";
    penalty = delayedVoltsRecovered ? scoring.warningDelayedRecoveredNoAmpPenalty : scoring.warningRecoveredNoAmpPenalty;
  } else if (voltsRecovered) {
    status = "pass";
    reason = "bus recovered; amps are not available";
    penalty = 0;
  } else if (postStartLow && amps.available && !amps.hasSpike) {
    status = "fail";
    reason = "post-start bus stayed below charging range without amp spike";
    penalty = scoring.failPostStartLowNoAmpPenalty;
  } else if (postStartLow) {
    status = "warning";
    reason = "post-start bus stayed below charging range";
    penalty = scoring.warningPostStartLowPenalty;
  } else {
    reason = "not enough post-start recovery evidence";
  }

  const charging = {
    status,
    reason,
    penalty,
    startTime,
    endTime: Number.isFinite(startTime) ? startTime + config.eventWindowSeconds : null,
    basis: event.basis,
    egtRise: event.egtRise,
    busPreMedian,
    busMin,
    busMinTime: busMinPoint?.elapsedSeconds ?? null,
    sagDepth,
    promptRecoveryMedian,
    sustainedRecoveryMedian,
    recoveryMedian,
    recoveryMin: recoveryExtent?.min ?? null,
    recoveryMax: recoveryExtent?.max ?? null,
    recoveryRise,
    chargingLimit,
    voltsRecovered,
    delayedVoltsRecovered,
    startAtLogBeginning,
    postStartLow,
    lowVoltsMaintained,
    voltageOnly: !amps.available,
    amps,
  };
  charging.note = chargingRecoveryNote(charging);
  return charging;
}

// Some aircraft depower the avionics bus to near zero during the engine
// power-down while the monitor keeps logging, so the bus voltage collapses to
// ~0 V at the tail of the log. That collapse is not a charging fault and must
// not drag down the running-voltage score. Detect a terminal run that ends the
// log below the depowered floor and never recovers, then absorb the steep
// descent leading into it (e.g. the knee while RPM is still spooling down).
// A genuine battery-only alternator failure stays ~11-12.5 V — above the floor —
// so it is not matched here and remains scored.
function terminalVoltageCollapseExclusion(data, { lowLimit, collapseFloor, leadSeconds }) {
  const finite = [];
  for (let index = 0; index < data.length; index += 1) {
    const buss = data[index]?.buss;
    if (Number.isFinite(buss)) finite.push({ index, buss, elapsed: data[index]?.elapsedSeconds });
  }
  if (!finite.length) return { indexSet: new Set(), window: null };
  const last = finite[finite.length - 1];
  if (!(last.buss < collapseFloor)) return { indexSet: new Set(), window: null };
  let core = finite.length - 1;
  while (core - 1 >= 0 && finite[core - 1].buss < collapseFloor) core -= 1;
  const coreOnset = finite[core];
  let start = core;
  while (start - 1 >= 0) {
    const prior = finite[start - 1];
    if (!(prior.buss < lowLimit)) break;
    if (Number.isFinite(prior.elapsed) && Number.isFinite(coreOnset.elapsed)
      && coreOnset.elapsed - prior.elapsed > leadSeconds) break;
    start -= 1;
  }
  const indexSet = new Set();
  for (let pos = start; pos < finite.length; pos += 1) indexSet.add(finite[pos].index);
  return {
    indexSet,
    window: {
      startTime: finite[start].elapsed ?? null,
      endTime: last.elapsed ?? null,
    },
  };
}

export function analyzeElectrical(data) {
  const runningConfig = electricalParameters.runningValues;
  const voltageConfig = electricalParameters.busVoltage;
  const values = data.map((point) => point.buss).filter(Number.isFinite);
  if (!values.length) {
    return { key: "electrical", score: null, note: "No bus voltage channel", details: {} };
  }
  const startExclusionWindows = electricalStartExclusionWindows(data);
  const isStartWindow = (point) => point.phase === "Startup"
    || (Number.isFinite(point.elapsedSeconds) && point.elapsedSeconds <= runningConfig.startWindowSeconds)
    || pointInElectricalStartWindow(point, startExclusionWindows);
  const startupPoints = data.filter((point) => isStartWindow(point) && Number.isFinite(point.buss));
  const baseRunningRows = data
    .map((point, index) => ({ point, index }))
    .filter(({ point }) => !isStartWindow(point) && point.phase !== "Shutdown" && Number.isFinite(point.buss));
  const poweredStates = data.map(electricalPowerRunningState);
  const lastPoweredIndex = poweredStates.lastIndexOf(true);
  const lastPoweredElapsed = data[lastPoweredIndex]?.elapsedSeconds;
  const isTerminalPowerOffTail = (point, index) => {
    if (!Number.isFinite(lastPoweredElapsed) || index <= lastPoweredIndex) return false;
    if (poweredStates[index] === true) return false;
    const elapsed = point.elapsedSeconds;
    return Number.isFinite(elapsed) && elapsed - lastPoweredElapsed > runningConfig.terminalGraceSeconds;
  };
  // The collapse floor/low-limit are system-dependent, so derive a provisional
  // 12V/28V verdict from the full-series median (the brief tail barely moves it,
  // and it matches the later isTwentyEightVolt verdict for real data).
  const provisionalMedian = median(values) ?? 0;
  const provisionalTwentyEightVolt = provisionalMedian > runningConfig.twentyEightVoltMedianThreshold;
  const provisionalVoltageScale = provisionalTwentyEightVolt ? 2 : 1;
  const provisionalLowLimit = (provisionalTwentyEightVolt ? voltageConfig.twentyEightVolt : voltageConfig.twelveVolt).lowLimit;
  const { indexSet: terminalCollapseSet, window: terminalCollapseWindow } = terminalVoltageCollapseExclusion(data, {
    lowLimit: provisionalLowLimit,
    collapseFloor: runningConfig.terminalCollapseFloorVolts * provisionalVoltageScale,
    leadSeconds: runningConfig.terminalCollapseLeadSeconds,
  });
  const terminalExcludedSamples = baseRunningRows.filter(({ point, index }) => isTerminalPowerOffTail(point, index)).length;
  const terminalCollapseExcludedSamples = terminalCollapseSet.size;
  const runningPoints = baseRunningRows
    .filter(({ point, index }) => !isTerminalPowerOffTail(point, index) && !terminalCollapseSet.has(index))
    .map(({ point }) => point);
  const runningValues = runningPoints.length >= Math.min(runningConfig.minRunningRows, values.length) ? runningPoints.map((point) => point.buss) : values;
  const medianVoltage = median(runningValues) ?? median(values) ?? 0;
  const isTwentyEightVolt = medianVoltage > runningConfig.twentyEightVoltMedianThreshold;
  const busConfig = isTwentyEightVolt ? voltageConfig.twentyEightVolt : voltageConfig.twelveVolt;
  const lowLimit = busConfig.lowLimit;
  const highLimit = busConfig.highLimit;
  const startSagLimit = busConfig.startSagLimit;
  const startSevereLimit = busConfig.startSevereLimit;
  const outRatio = runningValues.filter((value) => value < lowLimit || value > highLimit).length / runningValues.length;
  const runningExtent = extentFromValues(runningValues) ?? { min: 0, max: 0 };
  const fullExtent = extentFromValues(values) ?? runningExtent;
  const startupExtent = extentFromValues(startupPoints.map((point) => point.buss));
  const startMin = startupExtent?.min ?? null;
  const startMinPoint = startupPoints.reduce((best, point) => {
    if (!best || point.buss < best.buss) return point;
    return best;
  }, null);
  const outOfRangePoints = runningPoints.filter((point) => point.buss < lowLimit || point.buss > highLimit);
  const outOfRangeWindow = outOfRangePoints.length
    ? {
        startTime: outOfRangePoints[0].elapsedSeconds,
        endTime: outOfRangePoints.at(-1).elapsedSeconds,
      }
    : null;
  const voltageScale = isTwentyEightVolt ? 2 : 1;
  const startSag = Number.isFinite(startMin) && startMin < startSagLimit;
  const startSagPenalty = startSag
    ? Math.max(0, (startSagLimit - startMin) / voltageScale) * voltageConfig.startSagPenaltyWeight
      + Math.max(0, (startSevereLimit - startMin) / voltageScale) * voltageConfig.startSeverePenaltyWeight
    : 0;
  const runningPenalty = outRatio * voltageConfig.outOfRangePenaltyWeight
    + Math.max(0, lowLimit - runningExtent.min) * busConfig.lowPenaltyWeight
    + Math.max(0, runningExtent.max - highLimit) * busConfig.highPenaltyWeight;
  const runningStable = outRatio <= voltageConfig.stableOutRatio && runningExtent.min >= lowLimit && runningExtent.max <= highLimit;
  const chargingRecovery = analyzeStartChargingRecovery(data, {
    isTwentyEightVolt,
    lowLimit,
    runningStable,
    medianVoltage,
  });
  const score = clampScore(100 - runningPenalty - startSagPenalty);
  const runningLabel = runningStable
    ? `running ${medianVoltage.toFixed(1)} V stable`
    : `running ${runningExtent.min.toFixed(1)}-${runningExtent.max.toFixed(1)} V`;
  const note = startSag
    ? `Start sag ${startMin.toFixed(1)} V; ${runningLabel}`
    : outRatio > voltageConfig.noteOutRatio
      ? `Running voltage outside range ${Math.round(outRatio * 100)}%`
      : `Bus ${runningExtent.min.toFixed(1)}-${runningExtent.max.toFixed(1)} V`;
  return {
    key: "electrical",
    score,
    note,
    details: {
      min: fullExtent.min,
      max: fullExtent.max,
      runningMin: runningExtent.min,
      runningMax: runningExtent.max,
      runningMedian: medianVoltage,
      outRatio,
      lowLimit,
      highLimit,
      startMin,
      startMinTime: startMinPoint?.elapsedSeconds ?? null,
      startSag,
      startSagLimit,
      startSevereLimit,
      outOfRangeWindow,
      runningStable,
      isTwentyEightVolt,
      terminalExcludedSamples,
      terminalCollapseExcludedSamples,
      terminalCollapseWindow,
      startExclusionWindows,
      chargingRecovery,
    },
  };
}

// Decimated bus-voltage + amps trace for the Overview electrical visual.
// Bucketed min/max decimation (NOT a stride): brief alternator dropouts, regulator
// flicker, and the start sag are exactly what the graph must preserve, and a
// stride can step right over a few-sample excursion. Each time bucket emits its
// min and max sample in time order, so the drawn line still touches every
// extreme while a long cruise stays cheap (≤ 2 × maxBuckets points per channel).
// Pass startTime/endTime to window the trace (the start-recovery zoom does).
//
// Some monitors restart elapsedSeconds mid-log — a Garmin TXi keeps recording
// before the GPS time fix and resets the clock to 0 when timestamps arrive — so
// the artifact can carry an earlier segment whose elapsed range OVERLAPS the main
// timeline. Drawn as-is the trace doubles back in x (diagonal crossings). The
// recording is continuous across the seam, so re-baseline: the FINAL segment
// keeps its raw elapsed (the detector's window annotations reference it) and each
// earlier segment is shifted negative to butt up against the segment after it.
export function electricalBusTrace(data, { startTime = null, endTime = null, maxBuckets = 600 } = {}) {
  const stamped = (Array.isArray(data) ? data : [])
    .filter((point) => Number.isFinite(point?.elapsedSeconds))
    .map((point) => ({ point, t: point.elapsedSeconds }));
  const interval = sampleIntervalSeconds(stamped.map(({ point }) => point));
  let offset = 0;
  for (let index = stamped.length - 1; index > 0; index -= 1) {
    const previous = stamped[index - 1];
    const current = stamped[index];
    current.t += offset;
    if (previous.t > current.point.elapsedSeconds) {
      // Clock reset between previous and current: shift everything earlier so the
      // previous segment ends one sample interval before this segment starts.
      offset = current.t - interval - previous.t;
    }
  }
  if (stamped.length) stamped[0].t += offset;
  const rows = stamped.filter(({ t }) => {
    if (startTime !== null && t < startTime) return false;
    if (endTime !== null && t > endTime) return false;
    return true;
  });
  const channel = (key) => {
    const samples = rows
      .filter(({ point }) => Number.isFinite(point[key]))
      .map(({ point, t }) => ({ t, value: point[key] }));
    if (samples.length <= maxBuckets * 2) return samples;
    const bucketSpan = Math.max((samples[samples.length - 1].t - samples[0].t) / maxBuckets, 1e-6);
    const kept = [];
    let bucketEnd = samples[0].t + bucketSpan;
    let lo = null;
    let hi = null;
    const flush = () => {
      if (!lo) return;
      if (lo === hi) kept.push(lo);
      else if (lo.t <= hi.t) kept.push(lo, hi);
      else kept.push(hi, lo);
      lo = null;
      hi = null;
    };
    for (const sample of samples) {
      if (sample.t > bucketEnd) {
        flush();
        bucketEnd += bucketSpan * Math.max(1, Math.ceil((sample.t - bucketEnd) / bucketSpan));
      }
      if (!lo || sample.value < lo.value) lo = sample;
      if (!hi || sample.value > hi.value) hi = sample;
    }
    flush();
    return kept;
  };
  const volts = channel("buss");
  const amps = channel("amps");
  const times = [volts[0]?.t, volts[volts.length - 1]?.t, amps[0]?.t, amps[amps.length - 1]?.t].filter(Number.isFinite);
  return {
    volts,
    amps,
    hasVolts: volts.length >= 2,
    hasAmps: amps.length >= 2,
    tMin: times.length ? Math.min(...times) : null,
    tMax: times.length ? Math.max(...times) : null,
  };
}

export function analyzeStartChargeMetric(electrical) {
  const scoring = startChargeParameters.scoring;
  if (!electrical || electrical.score === null) {
    return { key: "charging", score: null, note: electrical?.note ?? "No bus voltage channel", details: {} };
  }
  const charging = electrical.details?.chargingRecovery;
  if (!charging) {
    return { key: "charging", score: null, note: "No engine start detected", details: {} };
  }
  if (charging.status === "pass") {
    return { key: "charging", score: charging.voltageOnly ? scoring.voltageOnlyPassScore : scoring.passScore, note: charging.note, details: charging };
  }
  if (charging.status === "warning") {
    return { key: "charging", score: clampScore(100 - Math.max(scoring.warningMinimumPenalty, charging.penalty ?? scoring.warningMinimumPenalty)), note: charging.note, details: charging };
  }
  if (charging.status === "fail") {
    return { key: "charging", score: clampScore(100 - Math.max(scoring.failMinimumPenalty, charging.penalty ?? scoring.failMinimumPenalty)), note: charging.note, details: charging };
  }
  return {
    key: "charging",
    score: null,
    note: charging.note || "Start charge not confirmed",
    details: charging,
  };
}
